/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { useRecoilValue } from 'recoil';
import {
  forwardRef,
  Fragment,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  Dispatch,
  SetStateAction,
  RefObject,
} from 'react';
import debounce from 'lodash/debounce';
import {
  alpha,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useRecoilState } from 'recoil';
import merge from 'lodash/merge';
import mergeWith from 'lodash/mergeWith';
import isEqual from 'lodash/isEqual';
import isPlainObject from 'lodash/isPlainObject';
import { ErrorBoundary } from 'react-error-boundary';
import { motion } from 'framer-motion';
import { Theme } from '@mui/material/styles';
import MicIcon from '@mui/icons-material/Mic';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { MagicWandIcon } from 'icons/MagicWand';
import MoviefoneNTrades from './MoviefoneNTrades';
import MoviefoneTradeCriteriaPanel from './MoviefoneTradeCriteria';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ExtendedStructureRequest,
  MoviefoneDetailView,
  ToolCall,
} from 'types/moviefone';
import { SUGGESTION_PROMPTS } from 'config/moviefone';
import {
  moviefoneNotesState,
  MoviefoneNotes,
  moviefoneTradeFiltersState,
} from 'states/moviefone';
import {
  executeTool,
  formatToolResultMessage,
  StreamingToolCallAccumulator,
  TOOL_DEFINITIONS,
  CLIENT_DEFINED_TOOL_DEFINITIONS,
  CLIENT_ONLY_POST_AMBLE,
} from 'util/toolCalling';
import { chatChunkMergeCustomizer } from 'util/moviefone';
import { selectTradeStructures } from 'util/moviefoneTradeSelection';
import {
  DefaultService as StreamAPI,
  OpenAPI,
  ErrorRsp,
  ChatCompletionModerationError,
} from 'gen/stream-api/requests';
import {
  getHeaders,
  getRequestBody,
  sendRequest,
} from 'gen/stream-api/requests/core/request';
import type { ApiRequestOptions } from 'gen/stream-api/requests/core/ApiRequestOptions';
import type { OnCancel } from 'gen/stream-api/requests/core/CancelablePromise';
import type {
  ChatMessage,
  ChatCompletionChunk,
  MoviefoneAnalysis,
  MoviefoneConversationSummary,
  SearchCandidate,
  StructureRequest,
  StructureRequestExtractionRsp,
} from 'gen/stream-api/requests/types.gen';
import { currentToastState } from 'states';
import { gzipJson } from 'util/shared/common';

const pickRandom = (arr: string[], n: number): string[] => {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, n);
};

const TOOL_DESCRIPTIONS: Record<string, string> = Object.fromEntries(
  TOOL_DEFINITIONS.map((t) => [t.name, t.description]),
);

const TRADE_CRITERIA_LLM_UPDATE_PREAMBLE = 'Per our conversation';

const TRADE_CRITERIA_LABELS: Record<string, string> = {
  symbol: 'Ticker Symbol',
  'view.horizon_days': 'Time Frame',
  'view.stock_direction': 'Direction',
  'bounds.min_reward_to_risk': 'Min Profit/Loss Ratio',
};

const MAX_CONTEXT_CHARS = 150_000;

/** Dotted paths of every leaf in `next` whose value differs from `prev`. */
const changedLeafPaths = (
  prev: unknown,
  next: unknown,
  prefix = '',
): [string, unknown][] => {
  if (isPlainObject(next)) {
    return Object.entries(next as Record<string, unknown>).flatMap(([k, v]) =>
      changedLeafPaths(
        isPlainObject(prev) ? (prev as Record<string, unknown>)[k] : undefined,
        v,
        prefix ? `${prefix}.${k}` : k,
      ),
    );
  }
  return isEqual(prev, next) ? [] : [[prefix, next]];
};

const humanizeToolName = (name: string): string =>
  name
    .split('_')
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ');

const getToolOutputError = (output: object): string | null => {
  const err = (output as { error?: unknown })?.error;
  return typeof err === 'string' ? err : null;
};

type ExtendedChatMessage = ChatMessage & {
  toolName?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toolOutput?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toolCalls?: any[];

  isError?: boolean;
  moviefoneAnalysis?: MoviefoneAnalysis;

  // The state of tradeCriteria, if there are any updates.
  tradeCriteria?: ExtendedStructureRequest;
  // The candidates picked from moviefoneAnalysis when it was fetched - see selectTradeStructures.
  selectedTradeStructures?: SearchCandidate[];
  // What the search engine can't do for this turn (an unsupported structure family), from
  // /extract-structure-request. Set instead of moviefoneAnalysis: no search is run.
  unsatisfiableCriteria?: string[];
};

/** The analysis (and the trade structures selected from it) a turn's /chat calls are
 * grounded in - possibly fetched by the turn itself, so newer than the `moviefoneAnalysis`
 * prop closure. */
type TurnGrounding = {
  analysis: MoviefoneAnalysis | null | undefined;
  selectedTradeStructures: SearchCandidate[] | undefined;
  // When set, the turn has no analysis: /chat explains these to the user instead.
  unsatisfiableCriteria?: string[];
};

/** True when the latest user turn asked for something the search engine can't do (see
 * `unsatisfiableCriteria`). Its other trade criteria still apply, but no analysis is
 * fetched for them until a later turn. */
function latestTurnIsUnsatisfiable(messages: ExtendedChatMessage[]): boolean {
  for (let i = messages.length - 1; i >= 0; i--) {
    if ((messages[i].unsatisfiableCriteria?.length ?? 0) > 0) {
      return true;
    }
    if (messages[i].role === 'user') {
      return false;
    }
  }
  return false;
}

/** Identity of one user turn, pinned before its first await and carried through any
 * tool-calling recursion. */
type TurnContext = TurnGrounding & {
  turnEpoch: number;
  turnTimestamp: string | null;
};

/** Compact, collapsed-by-default indicator for a tool call's data lookup. */
const ToolResultCard = ({
  msg,
  theme,
}: {
  msg: ExtendedChatMessage;
  theme: Theme;
}) => {
  const [expanded, setExpanded] = useState(false);
  const parsedContent: object = (() => {
    try {
      return JSON.parse(msg.content) as object;
    } catch {
      return {};
    }
  })();
  const error = getToolOutputError(parsedContent);
  const toolName = msg.toolName ?? ' ';
  const label = humanizeToolName(toolName);
  const description = TOOL_DESCRIPTIONS[toolName];

  if (error != null) {
    return (
      <Stack
        direction="row"
        spacing={0.75}
        sx={{
          alignItems: 'center',
          width: 'fit-content',
          maxWidth: '90%',
          marginBottom: '16px',
          padding: '8px 12px',
          borderRadius: '4px',
          border: `1px solid ${theme.palette.error.main}`,
          backgroundColor: alpha(theme.palette.error.main, 0.08),
        }}
      >
        <ErrorOutlineIcon
          fontSize="small"
          sx={{ color: theme.palette.error.main }}
        />
        <Typography
          variant="caption"
          sx={{ color: theme.palette.text.primary }}
        >
          Couldn&rsquo;t fetch {label.toLowerCase()}: {error}
        </Typography>
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        width: 'fit-content',
        maxWidth: '90%',
        marginBottom: '16px',
        borderRadius: '4px',
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        spacing={0.75}
        onClick={() => setExpanded((prev) => !prev)}
        title={description}
        sx={{
          alignItems: 'center',
          cursor: 'pointer',
          padding: '6px 10px',
        }}
      >
        <InsightsOutlinedIcon
          fontSize="small"
          sx={{ color: theme.palette.text.secondary }}
        />
        <Typography
          variant="caption"
          sx={{ color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}
        >
          Tool Completed &middot; {label}
        </Typography>
        <KeyboardArrowDownIcon
          fontSize="small"
          sx={{
            color: theme.palette.text.secondary,
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        />
      </Stack>
      {expanded && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            maxHeight: 300,
            overflow: 'auto',
            padding: '8px 10px',
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: alpha(theme.palette.common.white, 0.02),
          }}
        >
          {JSON.stringify(parsedContent, null, 2)}
        </Typography>
      )}
    </Box>
  );
};

/** A single message that fails to render (e.g. malformed leftover data from before a fix,
 * or any other unexpected shape) shouldn't take down the rest of the conversation with it. */
const MessageRenderErrorFallback = () => {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      spacing={0.75}
      sx={{
        alignItems: 'center',
        width: 'fit-content',
        marginBottom: '16px',
        padding: '8px 12px',
        borderRadius: '4px',
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <ErrorOutlineIcon
        fontSize="small"
        sx={{ color: theme.palette.text.secondary }}
      />
      <Typography
        variant="caption"
        sx={{ color: theme.palette.text.secondary }}
      >
        This message couldn't be displayed
      </Typography>
    </Stack>
  );
};

interface MoviefoneChatProps {
  tradeCriteria: ExtendedStructureRequest;
  setTradeCriteria: Dispatch<SetStateAction<ExtendedStructureRequest>>;
  moviefoneAnalysis?: MoviefoneAnalysis | null;
  setMoviefoneAnalysis: (a: MoviefoneAnalysis) => void;
  fetchMoviefoneAnalysis: ({
    riskFreeRate,
    tradeCriteria,
  }: {
    riskFreeRate?: number;
    tradeCriteria: StructureRequest;
  }) => Promise<MoviefoneAnalysis | null>;
  prevFetchMoviefoneAnalysisRef?: RefObject<null | Promise<null | MoviefoneAnalysis>>;
  riskFreeRate: number;
  /** What the detail panel is showing, so the trades it came from can highlight it. */
  detailView?: MoviefoneDetailView | null;
  onSelectStrategy?: (
    moviefoneAnalysis: MoviefoneAnalysis,
    searchCandidate: SearchCandidate,
  ) => void;
  onSelectCompareMode?: (
    moviefoneAnalysis: MoviefoneAnalysis,
    strategies: SearchCandidate[],
  ) => void;
  sym?: string;
  /** Fired the moment a brand-new conversation's first message is sent, before any
   * response (or the actual save) has come back - so a history list can show an
   * immediate placeholder rather than waiting on the full round-trip. */
  onNewConversationStarted?: () => void;
  /** Fired after a conversation is auto-saved *for the conversation currently on screen* -
   * marks it as the active one, so only call this for a save that's still relevant. */
  onConversationSaved?: (summary: MoviefoneConversationSummary) => void;
  /** Fired after ANY conversation is auto-saved, including one the user has since switched
   * away from - unlike onConversationSaved, safe to fire unconditionally since it only
   * refreshes a list, without marking anything as "active" or touching pending state. */
  onConversationListChanged?: (summary: MoviefoneConversationSummary) => void;
  /** Fired when a turn fails outright, so a "new conversation" placeholder doesn't get
   * stuck forever if the immediate save's follow-up turn never reaches the point where
   * it would normally resolve. */
  onConversationFailed?: () => void;
}

export interface MoviefoneChatHandle {
  sendMessage: (text: string) => void;
  // wasUnread: true if the caller had this conversation flagged unread (something saved
  // while it wasn't on screen) - skips the hard jump-to-bottom, since the user hasn't
  // actually seen whatever's down there yet.
  loadConversation: (timestamp: string, wasUnread: boolean) => void;
  startNewConversation: () => void;
}

// ---------------------------------------------------------------------------
// Moviefone chat API (generated client)
// ---------------------------------------------------------------------------

/** A non-ok HTTP response from the middle-layer, e.g. a gatekeeper rejection. */
class MoviefoneChatError extends Error {}

/**
 * Fetch the raw streaming Response for the middle-layer chat proxy, built
 * from the same request options / base URL / auth headers / interceptors
 * as the generated client (`StreamAPI.postV1MoviefoneChat`) would use —
 * but without buffering the body, so callers can read it incrementally.
 * The middle-layer handles system prompt, text_output, gatekeeper, and LLM.
 */
async function fetchMoviefoneChatStream(
  conversation: Array<{ role: string; content: string }>,
  moviefone_json: MoviefoneAnalysis | null | undefined,
  unsatisfiable_criteria: string[] | undefined,
  selected_trade_structures: SearchCandidate[] | undefined,
  userMessage: string | null,
  llmOverrides: MoviefoneNotes,
): Promise<Response> {
  const options: ApiRequestOptions = {
    method: 'POST',
    url: '/v1/moviefone/chat',
    mediaType: 'application/json',
    body: {
      conversation,
      moviefone_json,
      unsatisfiable_criteria,
      selected_trade_structures,
      user_message: userMessage,
      client_side_tools: CLIENT_DEFINED_TOOL_DEFINITIONS.map((td) => ({
        type: 'function',
        function: td,
      })),
      ...(llmOverrides.dirty
        ? {
            gatekeeper_model_override: llmOverrides.gatekeeperModel,
            gatekeeper_prompt_override: llmOverrides.gatekeeperPrompt,
            primary_model_override: llmOverrides.primaryModel,
            primary_prompt_override: llmOverrides.primaryPrompt,
          }
        : {}),
    },
  };

  const url = OpenAPI.BASE + options.url;
  const body = getRequestBody(options);
  const headers = await getHeaders(OpenAPI, options);
  const noopOnCancel = (() => {}) as unknown as OnCancel;

  return sendRequest(
    OpenAPI,
    options,
    url,
    body,
    undefined,
    headers,
    noopOnCancel,
  );
}

const CONVERSATION_TITLE_MAX_CHARS = 80;

/** Derives a title from the first user message, the same way the backend used to. */
function deriveConversationTitle(messages: ExtendedChatMessage[]): string {
  const firstUserMessage = messages
    .find((m) => m.role === 'user')
    ?.content?.trim();
  if (firstUserMessage == null || firstUserMessage === '') {
    return 'Untitled conversation';
  }
  const capitalized =
    firstUserMessage.charAt(0).toUpperCase() + firstUserMessage.slice(1);
  return capitalized.length > CONVERSATION_TITLE_MAX_CHARS
    ? `${capitalized.slice(0, CONVERSATION_TITLE_MAX_CHARS)}…`
    : capitalized;
}

/** HTTP header values must be ASCII, but a title can contain arbitrary Unicode (e.g. from the
 * user's own question) - `btoa` alone would throw on that, so encode via bytes first. Mirrors
 * the same base64 round-trip the backend does for the S3 tag it's ultimately stored as. */
function encodeTitleForHeader(title: string): string {
  const bytes = new TextEncoder().encode(title);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
}

/**
 * Store the conversation, built the same manual way as `fetchMoviefoneChatStream`
 * above - the generated client's `requestBody: string` signature would otherwise
 * double-JSON-encode an already-built body.
 */
async function storeMoviefoneConversation(
  conversation: ExtendedChatMessage[],
  title: string,
  existingTimestamp: string | null,
  interacted: boolean,
): Promise<MoviefoneConversationSummary> {
  const options: ApiRequestOptions = {
    method: 'POST',
    url: '/v1/moviefone/conversations',
    mediaType: 'application/json',
    body: await gzipJson(conversation),
    headers: {
      'content-encoding': 'gzip',
      'x-moviefone-title': encodeTitleForHeader(title),
      ...(existingTimestamp != null
        ? { 'x-moviefone-timestamp': existingTimestamp }
        : {}),
      ...(interacted ? { 'x-moviefone-interacted': 'true' } : {}),
    },
  };

  const url = OpenAPI.BASE + options.url;
  const headers = await getHeaders(OpenAPI, options);
  const noopOnCancel = (() => {}) as unknown as OnCancel;

  const response = await sendRequest(
    OpenAPI,
    options,
    url,
    options.body,
    undefined,
    headers,
    noopOnCancel,
  );
  if (!response.ok) {
    throw new MoviefoneChatError(
      `Failed to save conversation (${response.status})`,
    );
  }
  return (await response.json()) as MoviefoneConversationSummary;
}

function accumChatChunk(
  accum: ExtendedChatMessage,
  chunk: ChatCompletionChunk,
): ExtendedChatMessage {
  const result: ExtendedChatMessage = merge(accum, chunk);
  delete (result as unknown as Partial<ChatCompletionChunk>).choices;
  const delta = chunk.choices?.[0]?.delta;
  if (delta == null) {
    return result;
  }

  mergeWith(result, delta, chatChunkMergeCustomizer);

  return result;
}

/**
 * Call the middle-layer chat proxy and invoke `onChunk` for each parsed
 * SSE frame as it arrives, so the caller can render content incrementally.
 */
async function streamMoviefoneChat(
  conversation: Array<ChatMessage>,
  moviefoneJson: MoviefoneAnalysis | null | undefined,
  unsatisfiableCriteria: string[] | undefined,
  selectedTradeStructures: SearchCandidate[] | undefined,
  userMessage: string | null,
  onChunk: (chunk: ChatCompletionChunk) => void,
  llmOverrides: MoviefoneNotes,
): Promise<void> {
  const response = await fetchMoviefoneChatStream(
    conversation,
    moviefoneJson,
    unsatisfiableCriteria,
    selectedTradeStructures,
    userMessage,
    llmOverrides,
  );

  if (!response.ok) {
    let errBody: ErrorRsp | undefined;
    try {
      errBody = (await response.json()) as ErrorRsp;
    } catch {
      // response body wasn't JSON
    }
    throw new MoviefoneChatError(errBody?.msg ?? `HTTP ${response.status}`);
  }

  if (!response.body) {
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine.startsWith('data:')) {
        continue;
      }

      const data = trimmedLine.slice(5).trim();
      if (data === '' || data === '[DONE]') {
        continue;
      }

      try {
        onChunk(JSON.parse(data) as ChatCompletionChunk);
      } catch (error) {
        console.error('Skipping malformed SSE chunk', error);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MoviefoneChat = forwardRef<MoviefoneChatHandle, MoviefoneChatProps>(
  (
    {
      moviefoneAnalysis,
      tradeCriteria,
      setTradeCriteria,
      detailView,
      onSelectStrategy,
      onSelectCompareMode,
      sym,
      onNewConversationStarted,
      onConversationSaved,
      onConversationListChanged,
      onConversationFailed,
      fetchMoviefoneAnalysis,
      prevFetchMoviefoneAnalysisRef,
      riskFreeRate,
    },
    ref,
  ) => {
    const theme = useTheme();
    const [messages, setMessages] = useState<ExtendedChatMessage[]>([]);
    // Latest committed messages, for async callbacks that would otherwise see a stale closure.
    const messagesRef = useRef(messages);
    messagesRef.current = messages;
    const [input, setInput] = useState(
      'TSLA long call next 3 months what to buy',
    );
    const [isStreaming, setIsStreaming] = useState(false);
    const [followUps, setFollowUps] = useState<string[]>([]);
    const suggestions = useMemo(() => pickRandom(SUGGESTION_PROMPTS, 10), []);
    const lastMessageRef = useRef<HTMLDivElement>(null);
    const llmOverrides = useRecoilValue(moviefoneNotesState);
    const { riskTolerance, max_pnl_ratio } = useRecoilValue(
      moviefoneTradeFiltersState,
    );
    // Run right after each analysis fetch, so the selection reflects the trade filters and
    // criteria at that point rather than whatever they're later changed to.
    const selectTradeStructuresFor = (
      analysis: MoviefoneAnalysis,
      criteria: StructureRequest,
    ): SearchCandidate[] =>
      selectTradeStructures(
        analysis.structure_search?.candidates ?? [],
        riskTolerance,
        criteria.bounds?.min_reward_to_risk ?? 0,
        max_pnl_ratio,
      );
    const [toast, setToast] = useRecoilState(currentToastState);

    // `base` lets several patches chain without waiting on a re-render.
    const patchTradeCriteria = (
      update: Partial<StructureRequest>,
      base: ExtendedStructureRequest = tradeCriteria,
    ): ExtendedStructureRequest => {
      // the LLM may echo back a `provenance` field that isn't part of the schema
      delete (update as { provenance?: unknown }).provenance;
      // arrays (e.g. `structure.families`) replace rather than merge index-wise
      const updatedObj: ExtendedStructureRequest = mergeWith(
        {},
        base,
        update,
        (_obj: unknown, src: unknown) => (Array.isArray(src) ? src : undefined),
      );
      delete updatedObj.suppressFetchAnalysis;
      if (isEqual(updatedObj, base)) {
        return base;
      }
      setTradeCriteria(updatedObj);

      // Diffed against the pre-response criteria, so the last patch's toast lists every change.
      let message = TRADE_CRITERIA_LLM_UPDATE_PREAMBLE;
      for (const [path, v] of changedLeafPaths(tradeCriteria, updatedObj)) {
        message += `, ${TRADE_CRITERIA_LABELS[path] ?? path} updated to ${Array.isArray(v) ? v.join(', ') : String(v)}`;
      }

      setToast({ message, type: 'info', duration: 5_000 });
      return updatedObj;
    };

    const fetchMoviefoneAnalysisRef = useRef(async (): Promise<void> => {});
    fetchMoviefoneAnalysisRef.current = async () => {
      const fetchingEpoch = conversationEpochRef.current;
      if (prevFetchMoviefoneAnalysisRef?.current != null) {
        await prevFetchMoviefoneAnalysisRef.current;
      }
      const lastTradeCriteria = messagesRef.current.findLast(
        (m) => m.tradeCriteria != null,
      )?.tradeCriteria;
      if (
        isEqual(lastTradeCriteria, tradeCriteria) ||
        latestTurnIsUnsatisfiable(messagesRef.current)
      ) {
        return;
      }
      const newMoviefoneAnalysis = await fetchMoviefoneAnalysis({
        riskFreeRate,
        tradeCriteria,
      });
      // The user switched conversations while this was in flight - it belongs to the old one.
      if (
        newMoviefoneAnalysis == null ||
        conversationEpochRef.current !== fetchingEpoch
      ) {
        return;
      }
      const selectedTradeStructures = selectTradeStructuresFor(
        newMoviefoneAnalysis,
        tradeCriteria,
      );
      if (newMoviefoneAnalysis?.structure_search?.candidates?.length === 0) {
        setTradeCriteria({
          symbol: tradeCriteria.symbol,
          suppressFetchAnalysis: true,
        });
      }

      setMessages((prev) => {
        const lastMsg = prev[prev.length - 1];
        if (lastMsg?.role == 'tool' && lastMsg.moviefoneAnalysis == null) {
          return [
            ...prev.slice(0, -1),
            {
              ...lastMsg,
              moviefoneAnalysis: newMoviefoneAnalysis,
              tradeCriteria,
              selectedTradeStructures,
            },
          ];
        }
        return [
          ...prev,
          {
            role: 'tool',
            content: '',
            toolCalls: [{ name: 'patch_trade_criteria' }],
            moviefoneAnalysis: newMoviefoneAnalysis,
            tradeCriteria,
            selectedTradeStructures,
          },
        ];
      });
    };

    // True from the moment an analysis fetch is scheduled (i.e. before the debounce fires)
    // until the latest scheduled fetch settles. The generation guards against an earlier
    // fetch settling and clearing the flag while a newer one is still pending.
    const [isAnalysisPending, setIsAnalysisPending] = useState(false);
    const analysisGenRef = useRef(0);
    const fetchMoviefoneAnalysisDebouncerRef = useRef<ReturnType<
      typeof debounce<(gen: number) => void>
    > | null>(null);
    if (fetchMoviefoneAnalysisDebouncerRef.current == null) {
      fetchMoviefoneAnalysisDebouncerRef.current = debounce((gen: number) => {
        void fetchMoviefoneAnalysisRef.current().finally(() => {
          if (gen === analysisGenRef.current) {
            setIsAnalysisPending(false);
          }
        });
      }, 1_000);
    }
    const scheduleAnalysisFetch = () => {
      const gen = ++analysisGenRef.current;
      setIsAnalysisPending(true);
      // debounce invokes with the last call's args, i.e. the newest generation
      fetchMoviefoneAnalysisDebouncerRef.current?.(gen);
    };
    const cancelAnalysisFetch = useCallback(() => {
      fetchMoviefoneAnalysisDebouncerRef.current?.cancel();
      analysisGenRef.current += 1;
      setIsAnalysisPending(false);
    }, []);
    // Busy while the LLM is streaming or the analysis it triggered is still on its way.
    const isBusy = isStreaming || isAnalysisPending;

    // The latest criteria updateTradeCriteriaFromPrompt set, so the effect below can leave
    // their analysis to it rather than fetching a duplicate.
    const promptTradeCriteriaRef = useRef<ExtendedStructureRequest | null>(
      null,
    );

    const lastTradeCriteriaSentToAnalysis = messages.findLast(
      (m) => m.tradeCriteria != null,
    )?.tradeCriteria;
    const tradeCriteriaChanged = !isEqual(
      lastTradeCriteriaSentToAnalysis,
      tradeCriteria,
    );

    useEffect(
      () => {
        // don't fetch moviefone analysis if the user hasn't asked anything yet, for
        // criteria sendMessage extracted from a prompt - it fetches that analysis itself -
        // or while the latest turn asked for something the search engine can't do.
        if (
          isEqual(tradeCriteria, promptTradeCriteriaRef.current) ||
          latestTurnIsUnsatisfiable(messages) ||
          (tradeCriteria.suppressFetchAnalysis ?? false) ||
          !tradeCriteriaChanged ||
          !messages.some(
            (m) => m.role == 'user' && m.content?.trim()?.length > 0,
          )
        ) {
          return;
        }
        scheduleAnalysisFetch();
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        riskFreeRate,
        fetchMoviefoneAnalysisDebouncerRef,
        tradeCriteria,
        tradeCriteriaChanged,
      ],
    );

    // A loaded/saved conversation's timestamp, so re-saving overwrites it in place. Mirrored
    // into a ref since a turn's second save (post-response) needs it synchronously, before
    // a state update from the first save would be visible.
    const [activeConversationTimestamp, setActiveConversationTimestampState] =
      useState<string | null>(null);
    const activeConversationTimestampRef = useRef<string | null>(null);
    const setActiveConversationTimestamp = useCallback(
      (timestamp: string | null) => {
        activeConversationTimestampRef.current = timestamp;
        setActiveConversationTimestampState(timestamp);
      },
      [],
    );

    // Bumped on every conversation switch - used as a React key on the message list below,
    // so index-keyed component state (selected card, expanded tool result) doesn't leak
    // across two unrelated conversations.
    const [conversationEpoch, setConversationEpoch] = useState(0);
    // Mirrored into a ref so an in-progress turn can check the *current* epoch synchronously
    // and bail out if the user has since switched conversations, instead of writing stray
    // output into whatever's now on screen.
    const conversationEpochRef = useRef(0);
    // Timestamps with a turn still running in the background - checked on load so switching
    // to one shows the pending indicator instead of looking like nothing was asked.
    const inFlightTimestampsRef = useRef<Set<string>>(new Set());
    // Bumped on every handleLoadConversation call, so a slower-resolving earlier load can't
    // clobber a faster-resolving later one if the user switches conversations twice quickly.
    const loadConversationRequestRef = useRef(0);

    // Tracks which conversation the last scroll was for, so loading/switching (a full,
    // static message list) jumps straight to the bottom, while a turn actively streaming
    // into the *same* conversation keeps using the gentler top-anchored follow below.
    const lastScrolledEpochRef = useRef(-1);
    // Set for the switch about to happen when there's nothing settled to jump to yet -
    // still generating, or finished without the user ever having seen it. Consumed (and
    // reset) by the very next scroll.
    const skipHardJumpOnSwitchRef = useRef(false);
    const scrollToLatestMessage = useCallback(() => {
      const justSwitchedConversation =
        lastScrolledEpochRef.current !== conversationEpoch;
      lastScrolledEpochRef.current = conversationEpoch;
      if (justSwitchedConversation) {
        const skipHardJump = skipHardJumpOnSwitchRef.current;
        skipHardJumpOnSwitchRef.current = false;
        if (!skipHardJump) {
          lastMessageRef.current?.scrollIntoView({
            behavior: 'auto',
            block: 'end',
          });
          return;
        }
      }
      // 'auto' avoids each streamed token canceling the prior 'smooth' scroll mid-animation.
      // block: 'start' keeps a tall post's own top edge in view, rather than scrolling past it.
      lastMessageRef.current?.scrollIntoView({
        behavior: 'auto',
        block: 'start',
      });
    }, [conversationEpoch]);

    useEffect(() => {
      scrollToLatestMessage();
    }, [messages, followUps, scrollToLatestMessage]);

    // -----------------------------------------------------------------------
    // Strip UI-only fields (toolCalls/toolResult) before sending over the wire
    // -----------------------------------------------------------------------
    const buildConversation = useCallback(
      (userMessages: ExtendedChatMessage[]): ChatMessage[] => {
        const fieldsToKeep: (keyof ChatMessage)[] = [
          'role',
          'content',
          'tool_call_id',
          'tool_calls',
          'created',
          'id',
          'service_tier',
          'model',
        ] as const;
        const fullLog = userMessages
          .map((source) => {
            const result: Record<string, unknown> = {};
            if (
              -1 ==
                [
                  'assistant',
                  'developer',
                  'function',
                  'system',
                  'tool',
                  'user',
                ].indexOf(source.role) ||
              (source.role == 'tool' &&
                (source.tool_call_id == 'tool_call_-1' ||
                  source.tool_call_id == null ||
                  source.content == null))
            ) {
              return null;
            }
            for (const k of fieldsToKeep) {
              if (k in source) {
                if (k == 'tool_calls') {
                  // filter out injected toolcalls that don't belong in the main
                  // chat.
                  result[k] = source.tool_calls?.filter((tc) => tc.index >= 0);
                } else {
                  result[k] = source[k];
                }
              }
            }
            return result as unknown as ChatMessage;
          })
          .filter((m): m is ChatMessage => m != null);

        let charAccum = 0;
        const contextIndex = fullLog.findLastIndex((m) => {
          charAccum += JSON.stringify(m).length;
          return charAccum > MAX_CONTEXT_CHARS;
        });
        return fullLog.slice(Math.max(contextIndex, 0));
      },
      [],
    );

    // -----------------------------------------------------------------------
    // Saved conversations - saved automatically once each assistant turn
    // finishes, rather than requiring the user to remember to do it.
    // -----------------------------------------------------------------------
    const autoSaveConversation = useCallback(
      async (
        finalMessages: ExtendedChatMessage[],
        // True only when the user directly triggered this save - a response finishing never
        // counts, so the sidebar position only moves on real interaction.
        interacted: boolean,
        // Passed in rather than read fresh - by the time an abandoned turn's save actually
        // runs, activeConversationTimestampRef may have drifted to a different conversation.
        savingEpoch: number,
        existingTimestamp: string | null,
      ) => {
        try {
          // Unlike buildConversation (for the LLM request), storage keeps every field as-is -
          // a reloaded conversation needs toolName/isError/moviefoneAnalysis to render the
          // same way it looked live.
          const saved = await storeMoviefoneConversation(
            finalMessages,
            deriveConversationTitle(finalMessages),
            existingTimestamp,
            interacted,
          );
          // Fired first so the parent's "active" tracking is current by the time
          // onConversationListChanged checks it - this save's own timestamp must not end up
          // flagged unread against itself.
          if (conversationEpochRef.current === savingEpoch) {
            setActiveConversationTimestamp(saved.timestamp);
            onConversationSaved?.(saved);
          }
          onConversationListChanged?.(saved);
        } catch (error) {
          console.error('Failed to auto-save conversation', error);
          // Otherwise a failed save leaves a "new conversation" placeholder stuck forever -
          // nothing else would ever clear it.
          if (conversationEpochRef.current === savingEpoch) {
            onConversationFailed?.();
          }
        }
      },
      [
        onConversationSaved,
        onConversationListChanged,
        onConversationFailed,
        setActiveConversationTimestamp,
      ],
    );

    const handleLoadConversation = useCallback(
      async (timestamp: string, wasUnread: boolean) => {
        const requestId = ++loadConversationRequestRef.current;
        try {
          const loaded =
            (await StreamAPI.getV1MoviefoneConversationsByTimestamp({
              timestamp,
            })) as ExtendedChatMessage[];
          if (requestId !== loadConversationRequestRef.current) {
            return;
          }
          // Storage only has the last-saved state, which for one still generating is just
          // the question - append a placeholder for the answer on its way.
          const stillGenerating = inFlightTimestampsRef.current.has(timestamp);
          // Nothing settled to jump to yet in either case - still running, or the user
          // never actually saw whatever finished saving while they were elsewhere.
          skipHardJumpOnSwitchRef.current = stillGenerating || wasUnread;
          setMessages(
            stillGenerating
              ? [...loaded, { role: 'assistant', content: '' }]
              : loaded,
          );
          setActiveConversationTimestamp(timestamp);
          setFollowUps([]);
          conversationEpochRef.current += 1;
          setConversationEpoch(conversationEpochRef.current);
          setIsStreaming(stillGenerating);
          cancelAnalysisFetch();
        } catch (error) {
          console.error('Failed to load conversation', error);
        }
      },
      [setActiveConversationTimestamp, cancelAnalysisFetch],
    );

    const handleStartNewConversation = useCallback(() => {
      // Invalidate any load still in flight, so it can't resolve afterward and overwrite
      // this empty state with whatever conversation it was loading.
      loadConversationRequestRef.current += 1;
      setMessages([]);
      setActiveConversationTimestamp(null);
      setFollowUps([]);
      conversationEpochRef.current += 1;
      setConversationEpoch(conversationEpochRef.current);
      setIsStreaming(false);
      cancelAnalysisFetch();
    }, [setActiveConversationTimestamp, cancelAnalysisFetch]);

    // -----------------------------------------------------------------------
    // Follow-up suggestions
    // -----------------------------------------------------------------------
    const fetchFollowUps = async (
      conversationMessages: ExtendedChatMessage[],
      { analysis, unsatisfiableCriteria }: TurnGrounding,
    ) => {
      const fetchingEpoch = conversationEpochRef.current;
      try {
        const rsp = await StreamAPI.postV1MoviefoneChatFollowup({
          requestBody: {
            conversation: buildConversation(conversationMessages),
            moviefone_json:
              unsatisfiableCriteria != null ? undefined : analysis,
            unsatisfiable_criteria: unsatisfiableCriteria,
          },
        });
        if (conversationEpochRef.current !== fetchingEpoch) {
          return;
        }
        const match = /\[[\s\S]*\]/.exec(rsp.content);
        if (match) {
          const parsed = JSON.parse(match[0]) as string[];
          if (Array.isArray(parsed) && parsed.length > 0) {
            setFollowUps(parsed.slice(0, 5));
            return;
          }
        }
        setFollowUps(
          pickRandom(SUGGESTION_PROMPTS, 3 + Math.floor(Math.random() * 3)),
        );
      } catch {
        if (conversationEpochRef.current !== fetchingEpoch) {
          return;
        }
        setFollowUps(
          pickRandom(SUGGESTION_PROMPTS, 3 + Math.floor(Math.random() * 3)),
        );
      }
    };

    // -----------------------------------------------------------------------
    // Process tool calls (client-side execution, from streaming tool_calls)
    // -----------------------------------------------------------------------
    const processToolCallsFromStream = async (
      toolCalls: ToolCall[],
      currentMessages: ExtendedChatMessage[],
      // Inherited from the top-level turn, not re-read from the refs - the awaits below can
      // span a conversation switch, and this sequence must stay pinned to its own turn.
      turn: TurnContext,
    ): Promise<void> => {
      if (toolCalls.length === 0) {
        return;
      }

      // Trade criteria now come from /extract-structure-request before the turn starts, so
      // any patch_trade_criteria the LLM still emits is ignored.
      for (const toolCall of toolCalls.filter(
        (tc) => tc.name == 'patch_trade_criteria',
      )) {
        toolCall.result = null;
      }

      const { analysis } = turn;

      // Execute each tool
      let nonPatchToolsExecuted = false;
      if (analysis != null) {
        for (const toolCall of toolCalls.filter(
          (tc) => tc.name != 'patch_trade_criteria',
        )) {
          try {
            const result = await executeTool(
              toolCall.name,
              toolCall.args,
              analysis,
            );
            toolCall.result = result;
            nonPatchToolsExecuted = true;
          } catch (err) {
            console.error(
              `[MCP Tool] Failed to execute ${toolCall.name}:`,
              err,
            );
            toolCall.result = {
              error: `Tool execution failed: ${err instanceof Error ? err.message : 'unknown error'}`,
            };
          }
        }
      }

      // Build tool result messages
      const toolMessages: ExtendedChatMessage[] = toolCalls.map((tc) =>
        formatToolResultMessage(tc),
      );

      // Tool execution can outlast a conversation switch - keep updating the turn's own
      // messages regardless, but only push to screen if it's still the active one.
      const updatedMessages = [...currentMessages, ...toolMessages];

      if (conversationEpochRef.current === turn.turnEpoch) {
        setMessages(updatedMessages);
      }

      // determine if any of the calls are _not_ client-side, then re-execute:
      if (
        toolCalls.some(
          (tc) => !TOOL_DESCRIPTIONS[tc.name]?.includes(CLIENT_ONLY_POST_AMBLE),
        ) &&
        nonPatchToolsExecuted
      ) {
        // Pass the same turn identity through rather than letting the recursion re-derive it.
        await runAssistantTurn(updatedMessages, null, turn);
      }
    };

    // -----------------------------------------------------------------------
    // Run one assistant turn: post the conversation, accumulate the
    // response (content + any tool calls), then either hand off to tool
    // execution or fetch follow-up suggestions.
    // -----------------------------------------------------------------------
    const runAssistantTurn = async (
      updatedMessages: ExtendedChatMessage[],
      userMessage: string | null,
      // Pinned by the caller before any await of its own, so a conversation switch mid-turn
      // can never leak into a different conversation's identity partway through.
      turn: TurnContext,
      // The top-level call's own interacted save, still in flight (not awaited, so it
      // doesn't delay the spinner) - awaited below, right before this turn's own save, so a
      // slow one can't land on S3 after (and overwrite) this turn's fuller content.
      precedingSave: Promise<unknown> = Promise.resolve(),
    ) => {
      // Only sendMessage's call passes a real question; the tool-calling recursion passes
      // null, having already registered this turn as in-flight once.
      const isTopLevelCall = userMessage != null;
      const {
        turnEpoch,
        turnTimestamp,
        analysis,
        unsatisfiableCriteria,
        selectedTradeStructures,
      } = turn;
      const turnAbandoned = () => conversationEpochRef.current !== turnEpoch;
      if (isTopLevelCall && turnTimestamp != null) {
        inFlightTimestampsRef.current.add(turnTimestamp);
      }

      const assistantMessage: ExtendedChatMessage = {
        role: 'assistant',
        content: '',
      };
      // Mirrored into the local variable regardless, but a turn already abandoned by the
      // time it gets here (switched away while its own initial save was in flight) must not
      // touch what's actually on screen.
      updatedMessages = [...updatedMessages, assistantMessage];
      if (!turnAbandoned()) {
        setMessages(updatedMessages);
        setIsStreaming(true);
      }

      let accumulated = '';
      const toolCallAccumulator = new StreamingToolCallAccumulator();
      let sawToolCalls = false;
      try {
        await streamMoviefoneChat(
          // The trailing placeholder just added above isn't part of the conversation yet.
          buildConversation(updatedMessages.slice(0, -1)),
          // An unsatisfiable request ran no search, so there is no analysis to ground in.
          unsatisfiableCriteria != null ? undefined : analysis,
          unsatisfiableCriteria,
          selectedTradeStructures,
          userMessage,
          (chunk) => {
            const merged = accumChatChunk(
              updatedMessages[updatedMessages.length - 1],
              chunk,
            );
            updatedMessages = [...updatedMessages.slice(0, -1), merged];
            accumulated = merged.content;

            if (!turnAbandoned()) {
              setMessages(updatedMessages);
            }

            const delta = chunk.choices?.[0]?.delta;

            // Handle tool calls
            if (delta?.tool_calls != null) {
              sawToolCalls = true;
              toolCallAccumulator.addChunk(delta.tool_calls);
            }
          },
          llmOverrides,
        );

        // Check for tool calls first, then fall back to text
        if (sawToolCalls && toolCallAccumulator.hasToolCalls()) {
          const toolcalls = toolCallAccumulator.getToolCalls();
          await processToolCallsFromStream(toolcalls, updatedMessages, turn);
          return;
        }

        // Done - auto-save regardless of abandonment (the content still belongs in storage)
        // and fetch follow-ups (harmless if abandoned; fetchFollowUps guards its own UI).
        // Awaited: the finally block's reload needs the save to have landed first.
        if (accumulated.length > 0) {
          void precedingSave.then(
            () =>
              void autoSaveConversation(
                updatedMessages,
                false,
                turnEpoch,
                turnTimestamp,
              ),
          );
          void fetchFollowUps(updatedMessages, turn);
        }
      } catch (err) {
        console.error('[Moviefone Chat] Assistant turn failed:', err);
        if (!turnAbandoned()) {
          setMessages((prev) => {
            const updated = [...prev];
            updated[updated.length - 1] = {
              role: 'assistant',
              content: `SpotLine couldn't complete that response.  \n\n${(err as ChatCompletionModerationError)?.message ?? ''}  \n\n_Please try again or rephrase your question._`,
              isError: true,
            };
            return updated;
          });
        }
        // Save whatever made it through, regardless of abandonment - same reasoning as above.
        void precedingSave.then(
          () =>
            void autoSaveConversation(
              updatedMessages,
              false,
              turnEpoch,
              turnTimestamp,
            ),
        );
        // Only clear the pending indicator for the conversation still on screen - an
        // abandoned turn shouldn't clear it for a different, newer one.
        if (!turnAbandoned()) {
          onConversationFailed?.();
        }
      } finally {
        // A new turn may already be streaming in what's on screen now - don't stomp on it.
        if (!turnAbandoned()) {
          setIsStreaming(false);
        }
        if (isTopLevelCall && turnTimestamp != null) {
          inFlightTimestampsRef.current.delete(turnTimestamp);
          // User switched back to this still-generating conversation - reload now that
          // there's something to replace the pending placeholder with.
          if (
            turnAbandoned() &&
            activeConversationTimestampRef.current === turnTimestamp
          ) {
            // The user's still watching this exact conversation as it finishes, so jump to
            // the newly-settled bottom rather than treating it as unread.
            void handleLoadConversation(turnTimestamp, false);
          }
        }
      }
    };

    // -----------------------------------------------------------------------
    // Send message
    // -----------------------------------------------------------------------
    const sendMessage = async (text?: string) => {
      const trimmed = (text ?? input).trim();
      if (!trimmed || isBusy) {
        return;
      }

      const isFirstMessageOfConversation =
        messages.length === 0 && activeConversationTimestamp == null;
      if (isFirstMessageOfConversation) {
        onNewConversationStarted?.();
      }

      setFollowUps([]);
      const userMessage: ExtendedChatMessage = {
        role: 'user',
        content: trimmed,
      };
      const updatedMessages = [...messages, userMessage];
      // Spinner placeholder covers the criteria extraction / analysis before the reply.
      setMessages([...updatedMessages, { role: 'assistant', content: '' }]);
      setIsStreaming(true);
      setInput('');

      // Pinned before this save's own await - if the user switches conversations while it's
      // in flight, the streamed response below must still stay bound to this conversation,
      // not whatever's now current.
      const turnEpoch = conversationEpochRef.current;
      const timestampBeforeSave = activeConversationTimestampRef.current;

      // Save right away rather than waiting on the response. For a brand-new conversation
      // this assigns its real timestamp, which runAssistantTurn needs already landed, so
      // that case waits. An existing conversation's timestamp is already known - let this
      // save run in the background instead of delaying the turn (and its spinner) on it.
      const isNewConversation = timestampBeforeSave == null;
      const savePromise = autoSaveConversation(
        updatedMessages,
        true,
        turnEpoch,
        timestampBeforeSave,
      );
      if (isNewConversation) {
        await savePromise;
      }
      // An existing conversation's timestamp needs no further resolution. For a new one,
      // only trust the ref's post-save value if that save wasn't itself abandoned -
      // otherwise it now reflects whatever conversation the user switched to, not this one.
      let turnTimestamp = timestampBeforeSave;
      if (isNewConversation && conversationEpochRef.current === turnEpoch) {
        turnTimestamp = activeConversationTimestampRef.current;
      }
      // Registered here rather than only in runAssistantTurn, so switching away and back
      // during the criteria extraction below still shows the pending placeholder.
      if (turnTimestamp != null) {
        inFlightTimestampsRef.current.add(turnTimestamp);
      }

      const { messages: messagesWithAnalysis, ...grounding } =
        await updateTradeCriteriaFromPrompt(
          trimmed,
          updatedMessages,
          turnEpoch,
        );

      await runAssistantTurn(
        messagesWithAnalysis,
        trimmed,
        { turnEpoch, turnTimestamp, ...grounding },
        savePromise,
      );
    };

    // -----------------------------------------------------------------------
    // Extract trade criteria from the user's prompt, and if they changed, run
    // a fresh analysis and add it to the conversation ahead of the reply.
    // -----------------------------------------------------------------------
    const updateTradeCriteriaFromPrompt = async (
      userMessage: string,
      currentMessages: ExtendedChatMessage[],
      turnEpoch: number,
    ): Promise<TurnGrounding & { messages: ExtendedChatMessage[] }> => {
      const unchanged = {
        messages: currentMessages,
        analysis: moviefoneAnalysis,
        selectedTradeStructures: currentMessages.findLast(
          (m) => m.moviefoneAnalysis != null,
        )?.selectedTradeStructures,
      };
      const turnAbandoned = () => conversationEpochRef.current !== turnEpoch;

      let extracted: StructureRequestExtractionRsp;
      try {
        extracted = await StreamAPI.postV1MoviefoneExtractStructureRequest({
          requestBody: { user_message: userMessage },
        });
      } catch (err) {
        // e.g. a gatekeeper rejection - /chat will gatekeep and report it the same way.
        console.error(
          '[Moviefone Chat] Trade criteria extraction failed:',
          err,
        );
        return unchanged;
      }
      if (turnAbandoned()) {
        return unchanged;
      }

      // The backend sets unsatisfiable_criteria only when the structure family extractor
      // dropped the requested family; other extractors' `dropped:` notes stay in provenance.
      const { unsatisfiable_criteria, ...update } = extracted;
      const unsatisfiableCriteria =
        unsatisfiable_criteria != null && unsatisfiable_criteria.length > 0
          ? unsatisfiable_criteria
          : undefined;

      // The rest of the prompt's criteria (symbol, expiry, ...) apply either way.
      const newTradeCriteria = patchTradeCriteria(update);

      if (unsatisfiableCriteria != null) {
        // Claimed so the tradeCriteria effect doesn't fetch an analysis for these criteria
        // before runAssistantTurn puts this turn's messages on screen.
        promptTradeCriteriaRef.current = newTradeCriteria;
        // A search would answer a different question than the one asked, so skip it -
        // /chat explains what can't be done. latestTurnIsUnsatisfiable keeps the
        // tradeCriteria effect from fetching one for these criteria either.
        return {
          messages: [
            ...currentMessages,
            {
              role: 'tool',
              content: '',
              toolCalls: [{ name: 'extract_structure_request' }],
              unsatisfiableCriteria,
            },
          ],
          analysis: undefined,
          selectedTradeStructures: undefined,
          unsatisfiableCriteria,
        };
      }

      if (newTradeCriteria === tradeCriteria) {
        return unchanged;
      }
      promptTradeCriteriaRef.current = newTradeCriteria;

      const newAnalysis = await fetchMoviefoneAnalysis({
        riskFreeRate,
        tradeCriteria: newTradeCriteria,
      });
      if (newAnalysis == null || turnAbandoned()) {
        return unchanged;
      }
      const selectedTradeStructures = selectTradeStructuresFor(
        newAnalysis,
        newTradeCriteria,
      );
      if (newAnalysis.structure_search?.candidates?.length === 0) {
        setTradeCriteria({
          symbol: newTradeCriteria.symbol,
          suppressFetchAnalysis: true,
        });
      }

      const messagesWithAnalysis: ExtendedChatMessage[] = [
        ...currentMessages,
        {
          role: 'tool',
          content: '',
          toolCalls: [{ name: 'extract_structure_request' }],
          moviefoneAnalysis: newAnalysis,
          tradeCriteria: newTradeCriteria,
          selectedTradeStructures,
        },
      ];
      // Not set on screen here - runAssistantTurn does that right away, with its placeholder.
      return {
        messages: messagesWithAnalysis,
        analysis: newAnalysis,
        selectedTradeStructures,
      };
    };

    useImperativeHandle(ref, () => ({
      sendMessage: (text: string) => void sendMessage(text),
      loadConversation: (timestamp: string, wasUnread: boolean) =>
        void handleLoadConversation(timestamp, wasUnread),
      startNewConversation: handleStartNewConversation,
    }));

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void sendMessage();
      }
    };

    return (
      <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Messages Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px',
          }}
        >
          {messages.length === 0 && (
            <Typography
              variant="subtitle2"
              sx={{
                mb: 1,
                fontWeight: 600,
                color: theme.palette.text.primary,
                textAlign: 'center',
              }}
            >
              What stocks or trades are you interested in today?
            </Typography>
          )}
          {messages.length === 0 && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '8px',
                padding: '0 16px 16px',
              }}
            >
              {suggestions.map((s) => (
                <Chip
                  key={s}
                  label={s}
                  onClick={() => void sendMessage(s)}
                  sx={{
                    height: 'auto',
                    cursor: 'pointer',
                    color: `${theme.palette.sgGreen} !important`,
                    borderColor: theme.palette.sgGreen,
                    backgroundColor: 'transparent',
                    border: `1px solid ${theme.palette.sgGreen}`,
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '12.5px',
                    '& .MuiChip-label': {
                      whiteSpace: 'normal',
                      py: '4px',
                      fontSize: '12.5px',
                      color: 'inherit !important',
                    },
                    '&:hover, &.MuiChip-clickable:hover': {
                      borderColor: theme.palette.primary.main,
                      color: `${theme.palette.primary.main} !important`,
                      backgroundColor: 'rgba(45,212,168,0.08)',
                    },
                  }}
                  variant="outlined"
                />
              ))}
            </Box>
          )}
          {(() => {
            const renderedMessages = messages
              .filter((m) => m.role !== 'system')
              .filter(
                (m) =>
                  !(m.role == 'tool' && m.moviefoneAnalysis == null) &&
                  !(
                    m.role === 'assistant' &&
                    m.toolCalls != null &&
                    m.toolCalls.length > 0
                  ),
              );

            return (
              <Fragment key={conversationEpoch}>
                {renderedMessages.map((msg, idx) => {
                  const isLastMessage = idx === renderedMessages.length - 1;

                  if (msg.role === 'tool' && msg.moviefoneAnalysis != null) {
                    return (
                      <ErrorBoundary
                        key={idx}
                        FallbackComponent={MessageRenderErrorFallback}
                      >
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                        >
                          <Box ref={isLastMessage ? lastMessageRef : undefined}>
                            {msg.moviefoneAnalysis != null && (
                              <MoviefoneNTrades
                                moviefoneAnalysis={msg.moviefoneAnalysis}
                                selectedTradeStructures={
                                  msg.selectedTradeStructures!
                                }
                                onSelectTrade={onSelectStrategy}
                                onSelectCompareMode={onSelectCompareMode}
                                detailView={detailView}
                              />
                            )}
                            <ToolResultCard msg={msg} theme={theme} />
                          </Box>
                        </motion.div>
                      </ErrorBoundary>
                    );
                  }

                  return (
                    ((msg.content != null &&
                      msg.content.length > 0 &&
                      msg.content != 'null') ||
                      (isBusy && isLastMessage)) && (
                      <ErrorBoundary
                        key={idx}
                        FallbackComponent={MessageRenderErrorFallback}
                      >
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                        >
                          <Box
                            ref={isLastMessage ? lastMessageRef : undefined}
                            sx={{
                              marginBottom: '16px',
                              padding: '16px 18px',
                              borderRadius: '4px',
                              lineHeight: 1.65,
                              fontSize: '13.5px',
                              backgroundColor:
                                msg.role === 'user'
                                  ? theme.palette.mutedSgGreen
                                  : msg.isError
                                    ? alpha(theme.palette.error.main, 0.08)
                                    : theme.palette.background.paper,
                              color: theme.palette.text.primary,
                              border: `1px solid ${
                                msg.isError
                                  ? theme.palette.error.main
                                  : theme.palette.divider
                              }`,
                              width: '90%',
                              marginLeft:
                                msg.role === 'user' ? 'auto' : undefined,
                              marginRight:
                                msg.role === 'user' ? undefined : 'auto',
                              overflowX: 'auto',
                            }}
                          >
                            {msg.role === 'assistant' && msg.isError ? (
                              <Stack
                                direction="row"
                                spacing={1}
                                sx={{ alignItems: 'flex-start' }}
                              >
                                <ErrorOutlineIcon
                                  fontSize="small"
                                  sx={{
                                    color: theme.palette.error.main,
                                    mt: '1px',
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ color: theme.palette.text.primary }}
                                >
                                  <Markdown
                                    remarkPlugins={[
                                      [remarkGfm, { singleTilde: false }],
                                    ]}
                                  >
                                    {msg.content}
                                  </Markdown>
                                </Typography>
                              </Stack>
                            ) : msg.role === 'assistant' ? (
                              <Box
                                sx={{
                                  fontSize: '13.5px',
                                  lineHeight: 1.65,
                                  wordBreak: 'break-word',
                                  '& p': { m: 0, marginBottom: '6px' },
                                  '& p + p': { mt: 0 },
                                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                                    m: 0,
                                    mt: 1,
                                    mb: 0.5,
                                    fontSize: '16px',
                                    fontWeight: 700,
                                    color: theme.palette.primary.main,
                                  },
                                  '& h2': { color: theme.palette.text.primary },
                                  '& ul, & ol': { m: 0, pl: 2.5, py: 0.5 },
                                  '& li': { py: 0.125, marginBottom: '4px' },
                                  '& li + li': { mt: 0.25 },
                                  '& code': {
                                    fontFamily: 'monospace',
                                    fontSize: '0.8rem',
                                    backgroundColor: 'rgba(255,255,255,0.04)',
                                    px: 0.5,
                                    py: 0.125,
                                    borderRadius: 0.5,
                                  },
                                  '& pre': {
                                    m: 0,
                                    my: 0.75,
                                    p: 1,
                                    borderRadius: 1,
                                    backgroundColor: 'rgba(255,255,255,0.04)',
                                    overflow: 'auto',
                                    '& code': {
                                      backgroundColor: 'transparent',
                                      p: 0,
                                    },
                                  },
                                  '& blockquote': {
                                    m: 0,
                                    my: 0.5,
                                    pl: 1.5,
                                    borderLeft: `3px solid ${theme.palette.divider}`,
                                    color: theme.palette.text.primary,
                                  },
                                  '& strong': {
                                    fontWeight: 'bold',
                                    color: theme.palette.text.primary,
                                  },
                                  '& em': { fontStyle: 'italic' },
                                  '& hr': {
                                    border: 'none',
                                    borderTop: `1px solid ${theme.palette.divider}`,
                                    my: 1,
                                  },
                                  '& table': {
                                    borderCollapse: 'collapse',
                                    my: 0.5,
                                    width: '100%',
                                  },
                                  '& th': {
                                    textAlign: 'left',
                                    padding: '6px 10px',
                                    backgroundColor: 'rgba(255,255,255,0.04)',
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    color: theme.palette.text.primary,
                                    fontWeight: 600,
                                    fontSize: '11px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.4px',
                                    wordBreak: 'normal',
                                  },
                                  '& td': {
                                    padding: '5px 10px',
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    fontSize: '12.5px',
                                    wordBreak: 'normal',
                                  },
                                }}
                              >
                                {msg.content.length > 0 ? (
                                  <Markdown
                                    remarkPlugins={[
                                      [remarkGfm, { singleTilde: false }],
                                    ]}
                                  >
                                    {msg.content}
                                  </Markdown>
                                ) : (
                                  isStreaming &&
                                  isLastMessage && (
                                    <CircularProgress
                                      size={14}
                                      sx={{ color: theme.palette.primary.main }}
                                    />
                                  )
                                )}
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  color: 'inherit',
                                }}
                              >
                                {msg.content}
                              </Typography>
                            )}
                          </Box>
                        </motion.div>
                      </ErrorBoundary>
                    )
                  );
                })}
              </Fragment>
            );
          })()}
          {/* Follow-up suggestion chips */}
          {followUps.length > 0 && !isBusy && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                padding: '12px 20px 16px',
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              {followUps.map((s) => (
                <Chip
                  key={s}
                  label={s}
                  onClick={() => void sendMessage(s)}
                  sx={{
                    height: 'auto',
                    cursor: 'pointer',
                    color: `${theme.palette.text.primary} !important`,
                    borderColor: theme.palette.divider,
                    backgroundColor: 'transparent',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '12.5px',
                    '& .MuiChip-label': {
                      whiteSpace: 'normal',
                      py: '4px',
                      fontSize: '12.5px',
                      color: 'inherit !important',
                    },
                    '&:hover, &.MuiChip-clickable:hover': {
                      borderColor: theme.palette.primary.main,
                      color: `${theme.palette.primary.main} !important`,
                      backgroundColor: 'rgba(45,212,168,0.08)',
                    },
                  }}
                  variant="outlined"
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Chat Input Area */}
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '12px 20px 16px',
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <TextField
            size="small"
            placeholder="Ask SpotLine"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isBusy}
            multiline
            maxRows={3}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    data-ph-capture-attribute-element="moviefone-mic"
                    size="small"
                    edge="end"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    <MicIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    data-ph-capture-attribute-element="moviefone-magic-wand"
                    size="small"
                    edge="end"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    <MagicWandIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              width: '100%',
              maxWidth: '40ex',
              '& .MuiOutlinedInput-root': {
                backgroundColor: theme.palette.background.paper,
                borderRadius: '4px',
                fontSize: '13px',
                boxShadow: `0 0 10px 2px ${alpha(theme.palette.sgGreen, 0.5)}`,
                '& fieldset': {
                  borderColor: theme.palette.sgGreen,
                },
                '&:hover fieldset': { borderColor: theme.palette.sgGreen },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
              '& input::placeholder': {
                color: theme.palette.text.secondary,
                fontStyle: 'italic',
              },
            }}
          />
          <MoviefoneTradeCriteriaPanel
            tradeCriteria={tradeCriteria}
            setTradeCriteria={setTradeCriteria}
            sym={sym ?? ''}
            onSymChange={(newSym) =>
              void patchTradeCriteria({ symbol: newSym })
            }
            highlightIcon={
              toast?.message?.startsWith(TRADE_CRITERIA_LLM_UPDATE_PREAMBLE) ??
              false
            }
          />
        </Stack>

        <Typography
          variant="caption"
          sx={{
            padding: '8px 20px 12px',
            color: theme.palette.text.secondary,
            textAlign: 'center',
          }}
        >
          Disclaimer: All results are hypothetical, nothing should be taken as
          trading guidance or advice
        </Typography>
      </Stack>
    );
  },
);

export default MoviefoneChat;
